<script setup>

import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";

const emit = defineEmits(['open-in-slideout']);

const props = defineProps({
    note: {
        type: Object,
        required: true,
    }
})

const expanded = ref(false);
const overflowing = ref(false);
const contentEl = ref(null);
let resizeObserver = null;

// Attachments are images, PDFs or sound recordings; images and recordings are shown inline.
// The stored file extension comes from the detected content type, so it can be trusted here.
const attachmentPath = computed(() => props.note.attachment?.url?.split('?')[0] ?? '');
const attachmentIsImage = computed(() => /\.(jpe?g|png|gif|webp)$/i.test(attachmentPath.value));
const attachmentIsAudio = computed(() => /\.(mp3|m4a|wav|ogg|opus|webm|aac)$/i.test(attachmentPath.value));
const attachmentIsPdf = computed(() => /\.pdf$/i.test(attachmentPath.value));

const noteDate = computed(() => props.note.created_at?.user_tz || props.note.note_date);
const isEdited = computed(() => props.note.created_at?.user_tz !== props.note.updated_at?.user_tz);

const measure = () => {
    if (!contentEl.value || expanded.value) {
        return;
    }
    overflowing.value = contentEl.value.scrollHeight > contentEl.value.clientHeight + 1;
}

onMounted(() => {
    measure();
    if (window.ResizeObserver && contentEl.value) {
        resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(contentEl.value);
    }
})

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
})

watch(() => props.note.note, async () => {
    expanded.value = false;
    await nextTick();
    measure();
})

const openInSlideout = () => {
    emit('open-in-slideout', props.note);
}
</script>

<template>
    <article class="px-4 py-3">
        <header class="flex items-center justify-between gap-3">
            <div class="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs text-gray-500">
                <span
                    v-if="note.user"
                    class="truncate font-medium text-gray-700"
                >
                    {{ note.user.name }}
                </span>
                <span v-if="note.user" aria-hidden="true">&middot;</span>
                <time :datetime="noteDate">{{ noteDate }}</time>
                <span
                    v-if="isEdited"
                    class="text-gray-400"
                    :title="note.updated_at.user_tz"
                >
                    &middot; {{ _mft('shared:edited') }} {{ note.updated_at.user_tz }}
                </span>
            </div>

            <button-secondary
                size="sm"
                class="shrink-0"
                @click="openInSlideout"
            >
                <i class="fa-regular fa-pen mr-1"></i>
                {{ _mft('shared:action.edit') }}
            </button-secondary>
        </header>

        <div class="relative mt-1">
            <div
                ref="contentEl"
                class="note-content default-body-text-container whitespace-pre-line text-sm text-gray-800"
                :class="expanded ? '' : 'max-h-28 overflow-hidden'"
                v-html="note.note"
            ></div>
            <div
                v-if="overflowing && !expanded"
                class="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-b from-transparent to-white"
            ></div>
        </div>

        <button
            v-if="overflowing"
            type="button"
            class="mt-1 text-xs font-medium text-gray-500 hover:text-gray-900"
            @click="expanded = !expanded"
        >
            {{ expanded ? _mft('shared:expand.showLess') : _mft('shared:expand.showMore') }}
            <i
                class="fa-regular fa-chevron-down ml-0.5 text-xxs transition-transform"
                :class="expanded ? 'rotate-180' : ''"
            ></i>
        </button>

        <div v-if="note.attachment?.url" class="mt-3">
            <a
                v-if="attachmentIsImage"
                :href="note.attachment.url"
                target="_blank"
                class="mf-link-immune inline-block overflow-hidden rounded-lg border border-gray-200 hover:border-gray-300"
            >
                <img
                    :alt="_mft('crm:leadNoteCard.viewAttachment')"
                    :src="note.attachment.url"
                    loading="lazy"
                    class="h-28 w-auto max-w-full object-cover"
                />
            </a>
            <audio
                v-else-if="attachmentIsAudio"
                :src="note.attachment.url"
                controls
                preload="metadata"
                class="w-full max-w-sm"
            ></audio>
            <a
                v-else
                :href="note.attachment.url"
                target="_blank"
                class="mf-link-immune inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:border-gray-300 hover:bg-gray-50"
            >
                <i
                    class="fa-regular text-gray-400"
                    :class="attachmentIsPdf ? 'fa-file-pdf' : 'fa-paperclip'"
                ></i>
                {{ _mft('crm:leadNoteCard.viewAttachment') }}
            </a>
        </div>
    </article>
</template>

<style scoped>
.note-content {
    overflow-wrap: anywhere;
    word-break: break-word;
}

/* Note HTML comes from v-html, so links need :deep; mirrors mf-link, which backoffice pages do not apply to bare links */
.note-content :deep(a) {
    @apply underline decoration-gray-300 hover:decoration-wlPrimary cursor-pointer;
}
</style>
